<template>
  <div class="notary-verify verify-view">
    <div
      class="v-modal js-verify-view verify-view-modal"
      tabindex="0"
    >

      <el-card class="vv-central-container">
        <el-row>
          <el-col
            :span="24"
            class="vv-indicator-container">
            <div class="vv-indicator">
              <notary-progress-indicator
                v-if="isProgressIndicatorVisible"
                class="vv-indicator-progress"
                :progress-percent="progress"
              />
              <span
                class="vv-indicator-text"
                v-if="isProgressIndicatorVisible">
                {{ progressText }}
              </span>
              <span
                class="vv-indicator-pending"
                v-if="isPendingState"/>
              <span
                class="vv-indicator-fail"
                v-if="isFailState"/>
              <span
                class="vv-indicator-verified"
                v-if="isSuccessState"/>
            </div>
          </el-col>
        </el-row>

        <el-row>
          <el-col
            :span="24"
            class="vv-status-container">
            <span class="vv-status-block-text">
              <span v-if="isDownloadState">
                {{ $t('wrh-dashboard.notary.verify.view.status.downloading') }}
              </span>
              <span v-if="isVerifyState">
                {{ $t('wrh-dashboard.notary.verify.view.status.validating') }}</span>
              <span v-if="isPendingState">
                {{ $t('wrh-dashboard.notary.verify.view.status.pending') }}</span>
              <span v-if="isFailState">
                {{ $t('wrh-dashboard.notary.verify.view.status.fail') }}
              </span>
            </span>

            <a
              v-if="isSuccessState"
              class="vv-get-crt-button"
              target="_blank"
              :title="$t('wrh-dashboard.notary.verify.view.button.get-crt')"
              :href="notaryUrl + '/certificate/' + crtId" >
              <el-icon
                slot="icon"
                name="i-file-notarization-o--16"/>
              {{ $t('wrh-dashboard.notary.verify.view.button.get-crt') }}
            </a>
          </el-col>
        </el-row>

        <el-divider/>
        <el-row>
          <el-col
            :span="10"
            class="vv-ifo-col">
            <b>{{ $t('wrh-dashboard.notary.verify.view.file-info.name') }}</b>
          </el-col>
          <el-col
            :span="14"
            class="vv-ifo-col"
            style="text-align: right;">
            {{ fileNameText }}
          </el-col>
        </el-row>
        <el-divider/>

        <el-row>
          <el-col
            :span="10"
            class="vv-ifo-col">
            <b>{{ $t('wrh-dashboard.notary.verify.view.file-info.date-backed-up') }}</b>
          </el-col>
          <el-col
            :span="14"
            class="vv-ifo-col"
            style="text-align: right;">
            {{ crtDates.backedUpdDate | date }}
          </el-col>
        </el-row>
        <el-divider/>

        <el-row>
          <el-col
            :span="10"
            class="vv-ifo-col">
            <b>{{ $t('wrh-dashboard.notary.verify.view.file-info.date-notarized') }}</b>
          </el-col>
          <el-col
            :span="14"
            class="vv-ifo-col"
            style="text-align: right;">
            {{ crtDates.notarizationDate | date }}
          </el-col>
        </el-row>
        <el-divider/>

        <el-row>
          <el-col
            :span="10"
            class="vv-ifo-col">
            <b>{{ $t('wrh-dashboard.notary.verify.view.file-info.signed-by') }}</b>
          </el-col>
          <el-col
            :span="14"
            class="vv-ifo-col"
            style="text-align: right;">
            {{ notaryHostText }}
          </el-col>
        </el-row>
        <el-divider/>

        <el-row>
          <el-col
            :span="24"
            class="vv-ifo-button-col">
            <el-button
              class="vv-ifo-button "
              v-if="isProgressIndicatorVisible"
              @click="onCancel">
              {{ $t('wrh-dashboard.notary.verify.view.button.cancel') }}
            </el-button>
            <el-button
              class="vv-ifo-button"
              v-if="!isProgressIndicatorVisible"
              @click="onClose">
              {{ $t('wrh-dashboard.notary.verify.view.button.done') }}
            </el-button>
          </el-col>
        </el-row>

      </el-card>

    </div>

    <el-dialog
      size="medium"
      title=""
      :visible.sync="isUnsupportedBrowser">
      <div class="mx-24 my-16">
        <div class="mb-8">
          <div class="el-text el-text--size-m">
            {{ $t('wrh-dashboard.notary.warning.unsupported.browser.version') }}
          </div>
        </div>
      </div>
      <template #footer>
        <el-button @click="isUnsupportedBrowser = false">Ok</el-button>
      </template>
    </el-dialog>

  </div>
</template>

<script>
import 'isomorphic-fetch';
import axios from 'axios';
import * as CryptoJS from 'crypto-js';
import { debug } from '@/app/utils/debug';

import NotaryProgressIndicator from './NotaryProgressIndicator';


const NOTARY_STATE = {
  DOWNLOADING: 'download',
  VERIFY: 'verify',
  PENDING: 'pending',
  SUCCESS: 'success',
  FAIL: 'fail'
};

export default {
  name: 'NotaryVerify',
  components: {
    NotaryProgressIndicator
  },
  props: {
    archiveId: {
      type: String,
      default: '',
      required: true
    },
    fileName: {
      type: String,
      default: '',
      required: true
    },
    path: {
      type: String,
      default: '',
      required: true
    },
    fileVersion: {
      type: Number,
      default: 0,
      required: false
    },
    fileSizeBytes: {
      type: Number,
      default: 0,
      required: true
    },
    // msp only
    crtInternalId: {
      type: String,
      default: '',
      required: false
    },
    // wrh only
    certificateId: {
      type: String,
      default: '',
      required: false
    },
    // wrh only
    sequencer: {
      type: String,
      default: '',
      required: false
    },
    notaryUrl: {
      type: String,
      default: '',
      required: true
    },
    isEnterpriseMode: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data() {
    return {
      progress: 0,
      loadedBytes: 0,
      verifyReservedPercent: 0,
      fileNameText: '-',
      notaryDisplayDomain: '-',
      notaryHostText: '-',
      crtDates: {},
      isUnsupportedBrowser: false,
      verifyState: NOTARY_STATE.DOWNLOADING,
      isCancelled: false,
      uid: '',
      crtId: ''
    };
  },
  computed: {
    progressText() {
      return `${Math.min(Math.max(Math.round(this.progress), 0), 100)}%`;
    },
    isProgressIndicatorVisible() {
      return this.isDownloadState || this.isVerifyState;
    },
    isDownloadState() {
      return this.verifyState === NOTARY_STATE.DOWNLOADING;
    },
    isVerifyState() {
      return this.verifyState === NOTARY_STATE.VERIFY;
    },
    isPendingState() {
      return this.verifyState === NOTARY_STATE.PENDING;
    },
    isSuccessState() {
      return this.verifyState === NOTARY_STATE.SUCCESS;
    },
    isFailState() {
      return this.verifyState === NOTARY_STATE.FAIL;
    }
  },
  methods: {
    process(crtId) {
      const uid = this.guid();
      const cjs = this.cjsCreate();

      this.uid = uid;
      this.crtId = crtId;

      this.calculateHash(uid, cjs, crtId);
    },
    async mspProcess() {
      try {
        const response = await axios.get('/arCertificateId', {
          params: {
            archiveId: this.archiveId,
            fullPath: this.path,
            id: decodeURIComponent(this.crtInternalId)
          }
        });

        this.process(response.data.id);
      } catch (e) {
        console.log('/arCertificateId request error', e);
      }
    },
    async calculateHash(uid, cjs, crtId) {
      const response = await axios.get('/chunkRestore', {
        responseType: 'arraybuffer',
        params: {
          archiveId: this.archiveId,
          path: this.path,
          uid: uid,
          ver: this.fileVersion
        }
      });

      if (!this.isCancelled && (uid === response.headers.uid || debug)) {
        if ((response.status === 200 && response.headers['content-type']
          .indexOf('application/octet-binary') !== -1) || debug) {
          const buffer = new Uint8Array(response.data);
          this.updateProgress(buffer.length);

          const wordArray = CryptoJS.lib.WordArray.create(buffer);
          cjs.update(wordArray);

          if (!debug) {
            this.calculateHash(uid, cjs, crtId);
          }
        }
        if (response.status === 204 || debug) {
          const resultWords = cjs.finalize();
          const fileHash = CryptoJS.enc.Hex.stringify(resultWords);
          cjs.reset();

          if (this.isEnterpriseMode) {
            this.mspFullCheckFileHash(fileHash, this.crtInternalId);
          } else {
            this.checkFileHash(crtId, fileHash, this.sequencer);
          }
        }
        if (response.status !== 200 && response.status !== 204) {
          console.log('Notary /chunkRestore - file hash calc error:', response);
          this.isCancelled = true;
          this.stopChunksRestore(uid);
        }
      }
    },
    async stopChunksRestore(uid) {
      try {
        await axios.get('/stopChunkRestore', {
          params: { uid: uid }
        });
      } catch (e) {
        console.log('/stopChunkRestore request error', e);
      }
    },
    async notaryCertificate(crtId, sequencer) {
      try {
        const response = await axios.get('/crtDates', {
          params: {
            cert: crtId,
            sequencer: sequencer
          }
        });
        this.crtDates = response.data;
      } catch (e) {
        console.log('/crtDates request error', e);
      }
    },
    async checkFileHash(crtId, eTag, sequencer) {
      this.verifyState = NOTARY_STATE.VERIFY;
      const verifyUrl = `${this.notaryUrl}/api/v1/verify`;
      try {
        // don't use axios or need to remove all custom headers (Notary CORS limitation)
        const response =
          await fetch(`${verifyUrl}?cert=${encodeURIComponent(crtId)}&etag=${encodeURIComponent(eTag)}`);
        const data = await response.json();

        if (data !== undefined && data[crtId] !== undefined) {
          if (data[crtId] === 'true' || data[crtId] === true) {
            this.verifyState = NOTARY_STATE.SUCCESS;
            this.notaryCertificate(crtId, sequencer);
          } else if (data[crtId] === 'pending' || data[crtId] === 'inqueue') {
            this.verifyState = NOTARY_STATE.PENDING;
          } else if (data[crtId] === 'false' || data[crtId] === false) {
            this.verifyState = NOTARY_STATE.FAIL;
          } else {
            this.$notify.error({
              message: `Unsupported notary verify status: ${data[crtId]}`
            });
          }
        } else {
          this.$notify.error({
            message: `Notary crtId not found. ${JSON.stringify(data)}`
          });
        }
      } catch (e) {
        this.$notify.error({
          message: `Notary verify request error. ${verifyUrl}`
        });
        console.log('Notary verify request error', verifyUrl, e);
      }
    },
    async mspFullCheckFileHash(fileHash, crtInternalId) {
      try {
        const response = await axios.get('/calcMerkleTreeRoot', {
          params: {
            hash: fileHash,
            proof: JSON.stringify(crtInternalId.crt.hashList)
          }
        });

        this.checkFileHash(crtInternalId.id, response.data.root, crtInternalId.sequencer);
      } catch (e) {
        console.log('Notary verify request error', e);
      }
    },
    updateProgress(chunkSize) {
      this.verifyState = NOTARY_STATE.DOWNLOADING;
      this.loadedBytes += chunkSize;
      if (this.fileSizeBytes > 0 && this.loadedBytes > 0) {
        this.progress =
              ((100 / this.fileSizeBytes) * this.loadedBytes) - this.verifyReservedPercent;
      }
    },
    cjsCreate() {
      if (this.isEnterpriseMode) {
        return CryptoJS.algo.SHA256.create();
      }
      return CryptoJS.algo.MD5.create();
    },
    guid() {
      function s4() {
        return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
      }
      return `${s4() + s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
    },
    notaryDisplayHost() {
      if (this.notaryUrl !== '' && this.notaryUrl != null) {
        return this.notaryUrl
          .replace('http://', '')
          .replace('https://', '')
          .split('/')[0];
      }
      return '-';
    },
    isBrowserVersionSupported() {
      const match = /\b(MSIE |Trident.*?rv:|Edge\/)(\d+)/.exec(navigator.userAgent);
      return !(match && parseInt(match[2], 10) < 10);
    },
    init() {
      this.progress = 0;
      this.loadedBytes = 0;
      this.verifyReservedPercent = 2;
      this.fileNameText = this.fileName;
      this.crtDates = {};
      this.notaryHostText = this.notaryDisplayHost();
      this.isUnsupportedBrowser = false;
      this.verifyState = NOTARY_STATE.DOWNLOADING;
      this.isCancelled = false;
      this.crtId = '';
    },
    onCancel() {
      this.isCancelled = true;
      this.stopChunksRestore(this.uid);
      this.onClose();
    },
    onClose() {
      this.$emit('handleClose');
    }
  },
  mounted() {
    this.init();

    if (!this.isBrowserVersionSupported()) {
      this.isUnsupportedBrowser = true;
      return;
    }

    if (this.isEnterpriseMode) {
      this.mspProcess();
    } else {
      this.process(this.certificateId);
    }
  }
};

</script>

<style lang="scss" scoped>

$background-color: #00204d;
$info-container-width: 541px;

.verify-view {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 800;
    opacity: 0.9;
    filter: alpha(opacity=90);
    background: $background-color;
  }

  .verify-view-modal {
    opacity: 0.9;
    filter: alpha(opacity=90);
    background: $background-color;
    background-color: $background-color;
    overflow: auto;
  }

  .vv-central-container {
    margin-top: 3%;
    width: $info-container-width;
    margin-left: auto;
    margin-right: auto;
    background: $background-color;
    border: $background-color;
    padding: 16px;
    color: #FFFFFF;
  }

  .vv-ifo-col {
    line-height: 49px;
  }

.vv-ifo-button-col {
  text-align: center;
  line-height: 49px;
  padding-top: 29px;
}

.vv-status-container {
  text-align: center;
  line-height: 49px;
  padding-bottom: 24px;
}

.vv-ifo-button {
  color: #FFFFFF;
  border: solid 2px #FFFFFF;
  background-color: $background-color;
}

.vv-ifo-button:hover {
  border: solid 2px #FFFFFF;
  background-color: $background-color;
  opacity: 0.8;
  filter: alpha(opacity=80);
}

.vv-indicator-container {
  text-align: center;
}

.vv-get-crt-button {
  cursor: pointer;
  text-decoration: none;
  color: #FFFFFF;
}

.vv-get-crt-button:hover {
  text-decoration: none;
  color: #FFFFFF;
  opacity: 0.8;
  filter: alpha(opacity=80);
}

.vv-status-block-text {
  position: relative;
  top: 4px;
  text-align: center;
  font-size: 22px;
  line-height: 27px;
}
  .vv-indicator {
    position: relative;
    display: inline-block;
    text-align: center;
    background: url('../assets/notary/progress.svg') no-repeat, none;
    width: 256px;
    height: 256px;
    z-index: 801;
  }

  .vv-indicator-progress {
    position: absolute;
    display: inline-block;
    text-align: center;
    width: 256px;
    height: 256px;
    z-index: 802;
    left: 0;
    top: 0;
  }

  .vv-indicator-text {
    position: relative;
    color: #FFFFFF;
    text-align: center;
    font-size: 33px;
    top: 41%;
    z-index: 803;
  }

  .vv-indicator-verified {
    display: inline-block;
    text-align: center;
    background: url('../assets/notary/success.png') no-repeat;
    width: 256px;
    height: 256px;
    z-index: 804;
  }

  .vv-indicator-fail {
    display: inline-block;
    text-align: center;
    background: url('../assets/notary/fail.png') no-repeat;
    width: 256px;
    height: 256px;
    z-index: 804;
  }

  .vv-indicator-pending {
    display: inline-block;
    text-align: center;
    background: url('../assets/notary/pending.png') no-repeat;
    width: 256px;
    height: 256px;
    z-index: 804;
  }

</style>
